import { RadioEpisode as RadioEpisodeType } from '@aron/api';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ImageNg from '@fec/frontend/elements/media/image-ng/ImageNg';
import Button from '@fec/frontend/elements/buttons/button/button';
import {
  getA11yDuration,
  getFormattedDuration,
} from '@fec/assets/js/utils/media-progress';
import {
  formatDate,
  formatTime,
} from '@fec/assets/js/utils/date-time-formatter';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon';
import '@fec/frontend/foundation/translations/react_i18n_init';

import './RadioEpisode.scss';

type RadioEpisodeProps = {
  radioEpisode: RadioEpisodeType;
  isPreview?: boolean;
};
const RadioEpisode = forwardRef<HTMLAnchorElement, RadioEpisodeProps>(
  ({ radioEpisode, isPreview }: RadioEpisodeProps, ref) => {
    const { t } = useTranslation();
    const [isDownloaded, setIsDownloaded] = useState(false);

    const {
      squareImageProvider,
      squareImageUrl,
      altText,
      url,
      title,
      date,
      lead,
      episodeParts,
      downloadUrl,
      primaryChannel,
      assetId,
    } = radioEpisode;

    const image = {
      provider: squareImageProvider,
      id: squareImageUrl,
      alt: altText,
    };

    const isSameDay = (firstDate: Date, secondDate: Date) => {
      return (
        firstDate.getDate() === secondDate.getDate() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getFullYear() === secondDate.getFullYear()
      );
    };

    const episodeDate = (date: Date) => {
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (isSameDay(today, date)) {
        return t('today');
      } else if (isSameDay(tomorrow, date)) {
        return t('tomorrow');
      } else {
        return formatDate(date);
      }
    };

    return (
      <li
        className="radio-episode"
        data-referrer-track={`srg_mod3=${encodeURIComponent(radioEpisode.title)}&srg_mod4=${encodeURIComponent(radioEpisode.showTitle)}`}>
        <a
          href={`${url}${assetId ? '#autoplay' : ''}`}
          aria-hidden="true"
          tabIndex={-1}>
          <div className="radio-episode__image">
            <ImageNg
              // @ts-ignore
              image={image}
            />
            {assetId && (
              <div className="radio-episode__play-icon-container">
                <PlayIcon type="audio" />
              </div>
            )}
          </div>
        </a>
        <a ref={ref} href={url} className="radio-episode__link">
          <div className="radio-episode__description">
            {isPreview && (
              <span className="radio-episode__kicker">
                {`${episodeDate(new Date(date))}, ${formatTime(date)} ${t('oclock')}`}
                {primaryChannel && <>, {primaryChannel}</>}
              </span>
            )}
            <h4 className="radio-episode__title">{title}</h4>
            {!isPreview && (
              <RadioEpisodeCaption radioEpisode={radioEpisode} t={t} />
            )}
            {lead && <span className="radio-episode__lead">{lead}</span>}
            {!lead && episodeParts.length > 1 && (
              <>
                <p className="radio-episode__subtitle">
                  {t('radioEpisode.episodePartsTitle')}
                </p>
                <ul className="radio-episode__episode-parts">
                  {
                    // the first episodePart is the same as the episode title, therefore we skip it
                    episodeParts.slice(1).map((episodePart) => (
                      <li key={episodePart.id}>{episodePart.title}</li>
                    ))
                  }
                </ul>
              </>
            )}
          </div>
        </a>
        <div className="radio-episode__download">
          {downloadUrl && (
            <Button
              // @ts-ignore
              href={downloadUrl}
              onClick={() => setTimeout(() => setIsDownloaded(true), 1000)}
              styleModifier="button--neutral button--icon-only"
              svgIcon={classNames({
                'svg-icon--download-red': !isDownloaded,
                'svg-icon--download-success': isDownloaded,
              })}
              buttonText={t('radioEpisode.downloadButton')}
              additionalAttributes={{
                download: true,
                'data-download-event-track': downloadUrl.split('/').pop(),
                style: { margin: '0' },
              }}
            />
          )}
        </div>
      </li>
    );
  },
);

type RadioEpisodeCaptionProps = {
  radioEpisode: RadioEpisodeType;
  t: Function;
};
const RadioEpisodeCaption = ({ radioEpisode, t }: RadioEpisodeCaptionProps) => {
  const { durationMs, date } = radioEpisode;

  return (
    <div className="radio-episode__caption">
      {durationMs && (
        <>
          <span className="h-offscreen">
            {t('duration', {
              duration: getA11yDuration(durationMs, t),
            })}
          </span>
          <span aria-hidden="true">
            <span className="radio-episode__duration">
              {getFormattedDuration(durationMs)}
              &nbsp;
            </span>
            min,&nbsp;
          </span>
        </>
      )}
      {t('radioEpisode.caption', {
        date: formatDate(date),
        time: formatTime(date),
      })}
    </div>
  );
};

export default RadioEpisode;
